import React from 'react'
import _ from 'lodash'
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { useBidderDataUpdateBidderRegistrationAction } from '@sales/data/bidders/BidderDataActions';
import { useBidderDataGetSaleBidderDataQuery } from '@sales/data/bidders/BidderDataQueries';
import { getNextBidderNumber, SaleBidder } from '@sales/data/bidders/BidderData';
import { AgTechContact } from '@agtech/data/contacts/ContactData';
import ContactSelectionControl from '@agtech/web/integrations/contacts/components/selections/ContactSelectionControl';
import ContactDialog from '@agtech/web/integrations/contacts/dialogs/ContactDialog';

export type BidderRegistrationDialogProps = {
    bidder?: SaleBidder,
    onBidderRegistered?: (bidder: SaleBidder) => Promise<any>
}

const BidderRegistrationDialog = (props: BidderRegistrationDialogProps) => {
    const dialogQuery = useBidderDataGetSaleBidderDataQuery();
    const dialogAction = useBidderDataUpdateBidderRegistrationAction();

    return (
        <AgTechFormDialog
            width={550}
            dialogData={{
                query: dialogQuery,
                getDialogData: bidderData => {
                    const bidders = Object.values(bidderData.bidders);

                    return {
                        id: props.bidder?.id ?? 0,
                        contact: props.bidder?.contact,
                        bidderNumber: props.bidder?.bidderNumber ?? getNextBidderNumber(bidders),
                    };
                },
                getDialogContext: bidderData => ({
                    bidders: Object.values(bidderData.bidders)
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => ({
                    ...d,
                    contact: d.contact!
                })
            }}
            structure={() => ({
                header: 'Register Bidder',
                submitButtonText: 'Register',
            })}
            dialogBehavior={() => ({
                eventHandlers: {
                    onDialogSubmitted: async (submittedBidder) => {
                        if (props.onBidderRegistered) {
                            await props.onBidderRegistered(submittedBidder);
                        }
                    }
                }  
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <AgTechFormRow>
                        <ContactSelectionControl
                            formEntity={formContext.entity}
                            control={{ caption: 'Contact', isRequired: true, width: 65 }}
                            contacts={{
                                getSelectedContactId: ent => ent.contact?.id,
                                onContactSelected: async (ent, val) => ({
                                    ...ent,
                                    contact: val ? { ...val } : undefined
                                }),
                                filterContacts: cnt => formContext.context.bidders.find(b => b.contact?.id === cnt.id) === undefined,
                                customItems: items => ({
                                    ...items,
                                    itemCreationConfig: {
                                        noRecordsMessage: 'There are no contacts available for registration right now',
                                        noRecordsPrompt: 'To register a new contact, click the button below',
                                        creationButtonText: 'New Contact',
                                        creationButtonAction: e => {
                                            return new Promise<AgTechContact>((res, fail) => {
                                                e.dialogs.openDialog({
                                                    dialog: () => (
                                                        <ContactDialog onContactSubmitted={async createdContact => {
                                                            res(createdContact);
                                                        }} />
                                                    ),
                                                });
                                            });
                                        },
                                    }
                                })
                            }}
                        />
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption="Bidder Number"
                            required={true}
                            value={e => e.bidderNumber}
                            onControlValueChanged={async (e, val) => ({ ...e, bidderNumber: val })}
                            width={35}
                            readonly={props.bidder?.bidderNumber !== undefined}
                        />
                    </AgTechFormRow>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

BidderRegistrationDialog.Open = (e: AgTechClickEventHandler, props: BidderRegistrationDialogProps) => {
    e.dialogs.openDialog({
        dialog: () => <BidderRegistrationDialog {...props} />
    });
}

BidderRegistrationDialog.Button = (props: AgTechButtonProps & BidderRegistrationDialogProps) => {
    return (
        <AgTechButton
            text='Register Bidder'
            icon='bi bi-r-square-fill'
            {...props}
            action={e => BidderRegistrationDialog.Open(e, props)}
        />
    )
}

export default BidderRegistrationDialog